import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InvoiceSendOnboarding from "../../page/account/invoiceSendOnboarding";
import Spinner from "../../page/loading/spinner";
import docAid from "../../../assets/doc-aid.png";

// Helper to save doctor to localStorage
const saveDoctorToLocalStorage = (formData, planType, duration) => {
  const users = JSON.parse(localStorage.getItem("users")) || [];
  // Check if already exists
  const existingIndex = users.findIndex((u) => u.email === formData.email);
  const doctorData = {
    ...formData,
    role: "doctor",
    plan: {
      type: planType,
      duration: duration,
      startDate: new Date().toISOString(),
    },
    registeredAt: new Date().toISOString(),
  };
  if (existingIndex !== -1) {
    // Update existing user
    users[existingIndex] = { ...users[existingIndex], ...doctorData };
  } else {
    users.push(doctorData);
  }
  localStorage.setItem("users", JSON.stringify(users));
  // Also store current user in session
  sessionStorage.setItem("user", JSON.stringify(doctorData));
  sessionStorage.setItem("token", "demo-token");
};

const OnBoardingPayment = ({ value, varifyEmail, formData, componentRef }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceSend, setInvoiceSend] = useState(false);
  const [doctorId, setDoctorId] = useState(null);
  const navigate = useNavigate();

  // Plan data
  const plans = [
    {
      name: "FREE",
      duration: "3 Days",
      price: "0",
      features: ["Full Features of DOC-AID"],
      amount: 0,
      durat: 3,
    },
    {
      name: "BASIC",
      duration: "1 Year",
      price: "5000",
      features: [
        "Easy Appointment Booking",
        "Prescription Template",
        "Add New Clinic @ ₹250/- Only",
      ],
      amount: 5000,
      durat: 12,
    },
    {
      name: "Pro",
      duration: "6 Month",
      price: "8000",
      features: [
        "Easy Appointment Booking",
        "E-Prescription with full features",
        "Add New Clinic @ ₹500/- Only",
      ],
      amount: 8000,
      durat: 6,
    },
    {
      name: "Pro",
      duration: "1 Year",
      price: "15000",
      features: [
        "Easy Appointment Booking",
        "E-Prescription with full features",
        "Add New Clinic @ ₹500/- Only",
      ],
      amount: 15000,
      durat: 12,
    },
  ];

  // Mock payment processing
  const processPayment = useCallback(
    async (plan) => {
      setLoading(true);
      try {
        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Save doctor data with plan
        const doctorData = {
          ...formData,
          planType: plan.name,
          planDuration: plan.durat,
          planPrice: plan.price,
          paymentDate: new Date().toISOString(),
          paymentId: `mock_${Date.now()}`,
        };

        saveDoctorToLocalStorage(doctorData, plan.name, plan.durat);

        // Generate PDF if componentRef is provided
        if (componentRef && componentRef.current) {
          try {
            // Import dynamically to avoid circular dependency
            const { handleSavePDF } = await import(
              "../../page/term&condition/handleTermPrint"
            );
            await handleSavePDF(componentRef, doctorData.email); // pass email as id
          } catch (pdfError) {
            console.error("PDF generation failed:", pdfError);
          }
        }

        setDoctorId(doctorData.email); // using email as id for demo
        setInvoiceSend(true);

        toast.success("Payment successful! Welcome aboard.");
      } catch (error) {
        console.error("Payment error:", error);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [formData, componentRef]
  );

  const handlePlanSelection = useCallback(
    (plan) => {
      setSelectedPlan(plan);
      if (plan.name === "FREE") {
        // Free trial: directly process
        processPayment(plan);
      } else {
        // For paid plans, show a mock payment confirmation
        if (window.confirm(`Proceed to pay ₹${plan.price} for ${plan.duration}?`)) {
          processPayment(plan);
        }
      }
    },
    [processPayment]
  );

  const handleClose = () => {
    // If already saved, redirect to dashboard
    if (invoiceSend) {
      navigate("/dashboard");
    } else {
      value(); // call parent close function
    }
  };

  return (
    <div
      id="login-popup"
      tabIndex="-1"
      className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full flex items-center justify-center"
    >
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity">
            <div className="absolute inset-0 bg-gray-500 opacity-30"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-[95%] sm:w-full sm:p-6">
            <button
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
              onClick={handleClose}
            >
              <svg
                aria-hidden="true"
                className="w-8 h-8"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span className="sr-only">Close popup</span>
            </button>

            <section className="text-gray-900 body-font overflow-hidden">
              <div className="container px-5 py-24 mx-auto">
                <div className="flex flex-col text-center w-full mb-20">
                  <h1 className="sm:text-4xl text-3xl font-medium title-font mb-2">
                    Make The Wise Decision For Your Clinic
                  </h1>
                </div>
                <div className="flex flex-wrap -m-4">
                  {plans.map((plan, index) => (
                    <div
                      key={index}
                      className="p-4 xl:w-1/4 md:w-1/2 w-full text-gray-900"
                    >
                      <div className="group h-full p-6 rounded-lg border-2 border-gray-300 flex flex-col relative overflow-hidden text-gray-900 bg-white transition-all duration-300 hover:text-white hover:bg-gradient-to-tr hover:from-pink-300 hover:via-orange-500 hover:to-cyan-700">
                        <div className="flex flex-wrap">
                          <h2 className="w-2/3 text-sm tracking-widest title-font mb-1 font-medium">
                            {plan.name}
                          </h2>
                          <h2 className="w-1/3 py-1 px-3 rounded-xl items-end text-right text-white text-sm tracking-widest title-font mb-1 font-medium transition-all duration-300 group-hover:bg-black bg-orange-400">
                            {plan.duration}
                          </h2>
                        </div>

                        <h1 className="text-5xl pb-4 mb-4 border-b border-gray-200 leading-none">
                          <span className="text-3xl">₹ </span>{plan.price}
                        </h1>

                        <p className="font-semibold text-xl">What’s Included:</p>
                        {plan.features.map((feature, i) => (
                          <p key={i} className="flex items-center m-2">
                            <span className="w-4 h-4 mr-2 inline-flex items-center justify-center rounded-full flex-shrink-0 transition-colors duration-300">
                              <svg
                                width="19"
                                height="11"
                                viewBox="0 0 19 11"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0 6.21231L4.35301 10.5653L5.45099 9.45955L1.10577 5.11433M16.9993 0.11499L8.76052 8.36156L5.52108 5.11433L4.40752 6.21231L8.76052 10.5653L18.1051 1.22076M13.6976 1.22076L12.5996 0.11499L7.65475 5.05982L8.76052 6.1578L13.6976 1.22076Z"
                                  className="group-hover:fill-white fill-black transition-colors duration-300"
                                />
                              </svg>
                            </span>
                            {feature}
                          </p>
                        ))}

                        <button
                          onClick={() => handlePlanSelection(plan)}
                          disabled={loading}
                          className="mt-20 text-white bg-black border-0 py-2 px-4 w-full focus:outline-none group-hover:bg-white group-hover:text-black transition-all duration-300 rounded-xl text-center items-center disabled:opacity-50"
                        >
                          {loading ? "Processing..." : "Get Started"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {invoiceSend && (
        <InvoiceSendOnboarding
          reset={() => {
            setInvoiceSend(false);
            navigate("/dashboard");
          }}
          formData={formData}
          doctorId={doctorId}
        />
      )}

      {/* Loading Spinner */}
      {loading && <Spinner />}
      <ToastContainer />
    </div>
  );
};

export default OnBoardingPayment;